import React, { useState, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { VerifiedAddressInput } from '../Common/VerifiedAddressInput';
import { UserRole } from '../../types';
import { formatPhoneNumber } from '../../utils/phone';
import { calculateAge } from '../../utils/timeUtils';
import { 
  GraduationCap, 
  Briefcase, 
  UserCheck, 
  Handshake, 
  ChevronLeft, 
  ChevronRight,
  Calendar, 
  BookOpen, 
  MapPin, 
  Lock, 
  Search, 
  CheckCircle2, 
  ArrowRight,
  X,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Info,
  Building2,
  Wrench
} from 'lucide-react';
import { db, isUsingMockKeys } from '../../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getRegionConstraints, formatRegionWithNoun } from '../../utils/regionUtils';
import { isTraditionalSchool, isCareerTechCenter, isPostSecondarySchool, getPostSecondarySchoolsForRegion, deriveDistrictForSchoolName, deriveRegionIdForSchool, resolveDistrictForSchool } from '../../utils/schoolUtils';
import { isEmailDomainProhibited } from '../../utils/studentUtils';
import { useInAppDialog } from '../Common/InAppDialog';

// Hardcoded high schools in the region for the demo
const REGIONAL_SCHOOLS: string[] = [];

// Helper: Districts matching the active workspace tenant
const getDistrictsForTenant = (tenantId: string): string[] => {
  try {
    const constraints = getRegionConstraints(tenantId, '');
    if (constraints && constraints.districts && constraints.districts.length > 0) {
      return constraints.districts;
    }
  } catch (e) {
    console.warn("Failed to get region constraints for tenant:", tenantId, e);
  }
  if (tenantId === 'apextech' || tenantId === 'training') {
    return ['Training School District', 'Spartanburg School District 7', 'Northeast Academic District'];
  }
  return ['General School District', 'Technical Education Center'];
};

// Helper: High schools matching the active workspace tenant
const getSchoolsForTenant = (tenantId: string): string[] => {
  try {
    const constraints = getRegionConstraints(tenantId, '');
    if (constraints && constraints.schools && constraints.schools.length > 0) {
      return constraints.schools;
    }
  } catch (e) {
    console.warn("Failed to get region constraints for tenant:", tenantId, e);
  }
  if (tenantId === 'apextech' || tenantId === 'training') {
    return ['Bayside High School', 'Valley View High School', 'Oceanfront Academy', 'R. D. Anderson Applied Tech'];
  }
  return ['Central High', 'Westside Tech', 'Eastside Academy'];
};

const isSchoolInDistrict = (schoolName: string, districtName: string): boolean => {
  const sNorm = schoolName.toLowerCase();
  const dNorm = districtName.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').trim(); // strip region suffixes like "(spartanburg)" from district name

  // Spartanburg mapping
  if (dNorm.includes('spartanburg school district 1') || dNorm === 'spartanburg 1' || dNorm.includes('spartanburg 1')) {
    return sNorm.includes('campobello') || sNorm.includes('chapman') || sNorm.includes('landrum');
  }
  if (dNorm.includes('spartanburg school district 2') || dNorm === 'spartanburg 2' || dNorm.includes('spartanburg 2')) {
    return sNorm.includes('boiling springs') || sNorm.includes('chesnee') || sNorm.includes('swofford');
  }
  if (dNorm.includes('spartanburg school district 3') || dNorm === 'spartanburg 3' || dNorm.includes('spartanburg 3')) {
    return sNorm.includes('broome');
  }
  if (dNorm.includes('spartanburg school district 4') || dNorm === 'spartanburg 4' || dNorm.includes('spartanburg 4')) {
    return sNorm.includes('woodruff');
  }
  if (dNorm.includes('spartanburg school district 5') || dNorm === 'spartanburg 5' || dNorm.includes('spartanburg 5')) {
    return sNorm.includes('byrnes') || sNorm.includes('r.d. anderson') || sNorm.includes('rd anderson') || sNorm.includes('r. d. anderson');
  }
  if (dNorm.includes('spartanburg school district 6') || dNorm === 'spartanburg 6' || dNorm.includes('spartanburg 6')) {
    return sNorm.includes('dorman');
  }
  if (dNorm.includes('spartanburg school district 7') || dNorm === 'spartanburg 7' || dNorm.includes('spartanburg 7')) {
    return sNorm.includes('spartanburg high') || sNorm.includes('daniel morgan') || sNorm.includes('whitlock');
  }

  // Richland mapping
  if (dNorm.includes('richland school district one') || dNorm.includes('richland 1')) {
    return sNorm.includes('columbia') || sNorm.includes('dreher');
  }
  if (dNorm.includes('richland school district two') || dNorm.includes('richland 2')) {
    return sNorm.includes('spring valley') || sNorm.includes('ridge view');
  }

  // Lexington mapping
  if (dNorm.includes('lexington county school district one') || dNorm.includes('lexington 1')) {
    return sNorm.includes('lexington') || sNorm.includes('river bluff');
  }
  if (dNorm.includes('lexington school district two') || dNorm.includes('lexington 2')) {
    return sNorm.includes('airport') || sNorm.includes('brookland-cayce');
  }

  // York mapping
  if (dNorm.includes('rock hill') || dNorm.includes('york 3') || dNorm.includes('district three') || dNorm.includes('district 3')) {
    return sNorm.includes('rock hill') || sNorm.includes('northwestern');
  }
  if (dNorm.includes('fort mill') || dNorm.includes('york 4') || dNorm.includes('district four') || dNorm.includes('district 4')) {
    return sNorm.includes('fort mill');
  }

  // If there's a parenthetical region suffix match, verify it
  const sRegionMatch = sNorm.match(/\(([^)]+)\)/);
  const dRegionMatch = districtName.toLowerCase().match(/\(([^)]+)\)/);
  if (sRegionMatch && dRegionMatch) {
    return sRegionMatch[1] === dRegionMatch[1];
  }

  return true;
};



export interface JoinOrgContext {
  orgId: string;
  orgName: string;
  primaryRegion?: string;
  extendedRegions?: string[];
  address?: string;
  email?: string;
  name?: string;
  title?: string;
  phone?: string;
}

export const SetupWizard: React.FC<{ 
  onComplete: () => void; 
  showClose?: boolean; 
  hideSidebar?: boolean; 
  initialRole?: UserRole | null;
  joinOrgContext?: JoinOrgContext | null;
}> = ({ 
  onComplete, 
  showClose = true, 
  hideSidebar = false, 
  initialRole = null,
  joinOrgContext = null
}) => {
  const { signUpWithRoleAndData, updateUserProfile, activeUser, activeTenant, tenants, changeTenant, setCurrentView, systemConfig, globalBranding } = useWBL();
  const { showAlert } = useInAppDialog();
  const isAlreadyLoggedIn = !!activeUser;
  const isMultiTenant = systemConfig?.multiTenantEnabled !== false;

  const [step, setStep] = useState(initialRole ? 2 : 1);
  const [role, setRole] = useState<UserRole | null>(initialRole || activeUser?.role || null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [formData, setFormData] = useState<any>(() => {
    if (activeUser) {
      const parts = (activeUser.name || '').split(' ');
      const contactFirstName = parts[0] || '';
      const contactLastName = parts.slice(1).join(' ') || '';
      return {
        contactFirstName: activeUser.contactFirstName || contactFirstName,
        contactLastName: activeUser.contactLastName || contactLastName,
        dateOfBirth: activeUser.dateOfBirth || '',
        academicLevel: activeUser.academicLevel || '',
        school: activeUser.school || '',
        classYear: activeUser.classYear || '',
        district: activeUser.district || '',
        orgName: activeUser.orgName || '',
        address: activeUser.address || '',
        parentPhone: activeUser.parentPhone || ''
      };
    }
    return {};
  });

  // Automatically detect and lock organization team invite context (from props or URL)
  useEffect(() => {
    let ctx = joinOrgContext;
    if (!ctx && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('joinOrg') === '1' || params.get('orgInvite') === 'true') {
        const extRaw = params.get('extendedRegions') || '';
        ctx = {
          orgId: params.get('orgId') || '',
          orgName: params.get('orgName') || '',
          primaryRegion: params.get('primaryRegion') || params.get('region') || '',
          extendedRegions: extRaw ? extRaw.split(',').map(r => r.trim()).filter(Boolean) : [],
          address: params.get('address') || '',
          email: params.get('email') || '',
          name: params.get('name') || '',
          title: params.get('title') || '',
          phone: params.get('phone') || ''
        };
      }
    }

    if (ctx && ctx.orgName) {
      setRole('organization');
      setStep(2);
      const nameParts = (ctx.name || '').trim().split(/\s+/);
      const cFirst = nameParts[0] || '';
      const cLast = nameParts.slice(1).join(' ') || '';

      const matchedTenant = ctx.primaryRegion
        ? tenants.find(t => t.id === ctx!.primaryRegion || t.subdomain === ctx!.primaryRegion || t.name.toLowerCase() === ctx!.primaryRegion?.toLowerCase())
        : undefined;
      if (matchedTenant && activeTenant.id !== matchedTenant.id) {
        changeTenant(matchedTenant.id);
      }

      setFormData((prev: any) => ({
        ...prev,
        isJoinOrg: true,
        orgId: ctx!.orgId,
        parentOrgId: ctx!.orgId,
        orgTeamId: ctx!.orgId,
        orgName: ctx!.orgName,
        tenantId: matchedTenant?.id || ctx!.primaryRegion || prev.tenantId,
        region: matchedTenant?.name || prev.region,
        address: ctx!.address || prev.address || '',
        extendedRegions: ctx!.extendedRegions || [],
        contactFirstName: cFirst || prev.contactFirstName || '',
        contactLastName: cLast || prev.contactLastName || '',
        professionalTitle: ctx!.title || prev.professionalTitle || '',
        contactPhone: ctx!.phone || prev.contactPhone || ''
      }));

      if (ctx.email) {
        setEmail(ctx.email);
      }
    }
  }, [joinOrgContext]);
  const [dobError, setDobError] = useState<string | null>(null);
  const [email, setEmail] = useState(activeUser?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [provideParentEmail, setProvideParentEmail] = useState(false);
  const [studentAttested, setStudentAttested] = useState(false);
  const STUDENT_LEGAL_ATTESTATION = "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian.";
  const [searchTerm, setSearchTerm] = useState('');
  const [dbDistricts, setDbDistricts] = useState<any[]>([]);
  const [dbSchools, setDbSchools] = useState<any[]>([]);

  const hasMinLength = password.length >= 8;
  const hasCapital = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  useEffect(() => {
    if (activeUser && activeUser.role && !role) {
      setRole(activeUser.role);
    }
  }, [activeUser]);

  useEffect(() => {
    if (isUsingMockKeys) {
      const mockDistricts = getDistrictsForTenant(activeTenant.id);
      mockDistricts.sort((a, b) => a.localeCompare(b));
      const dists = mockDistricts.map(name => ({ id: name, name }));
      setDbDistricts(dists);
      
      const mockSchools = getSchoolsForTenant(activeTenant.id);
      mockSchools.sort((a, b) => a.localeCompare(b));
      const schoolObjs = mockSchools.map(name => ({ id: name, name }));
      setDbSchools(schoolObjs);

      // If active user already has a school selected, reverse-map the district
      if (formData.school || formData.pendingSchool) {
        const resolved = resolveDistrictForSchool(formData.school || formData.pendingSchool, activeTenant.id, schoolObjs, dists);
        if (resolved.districtName) {
          setFormData(prev => ({
            ...prev,
            district: resolved.districtName,
            districtId: resolved.districtId
          }));
        }
      } else if (dists.length === 1 && !formData.district) {
        setFormData(prev => ({ ...prev, district: dists[0].name, districtId: dists[0].id }));
      }
      return;
    }

    // Live Firestore subscription
    const districtsQuery = query(
      collection(db, 'districts'),
      where('regionId', '==', activeTenant.id)
    );

    const unsubscribeDistricts = onSnapshot(districtsQuery, (districtsSnap) => {
      const districtsList = districtsSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.id
      }));

      // Sort districts alphabetically
      districtsList.sort((a, b) => a.name.localeCompare(b.name));

      if (districtsList.length === 0) {
        const fallbackDistricts = getDistrictsForTenant(activeTenant.id);
        fallbackDistricts.sort((a, b) => a.localeCompare(b));
        const fallbackDistObjs = fallbackDistricts.map(name => ({ id: name, name }));
        setDbDistricts(fallbackDistObjs);
        
        const fallbackSchools = getSchoolsForTenant(activeTenant.id).map(name => ({ id: name, name }));
        fallbackSchools.sort((a, b) => a.name.localeCompare(b.name));
        setDbSchools(fallbackSchools);

        if (formData.school || formData.pendingSchool) {
          const resolved = resolveDistrictForSchool(formData.school || formData.pendingSchool, activeTenant.id, fallbackSchools, fallbackDistObjs);
          if (resolved.districtName) {
            setFormData(prev => ({ ...prev, district: resolved.districtName, districtId: resolved.districtId }));
          }
        }
        return;
      }

      setDbDistricts(districtsList);
      
      if (formData.school || formData.pendingSchool) {
        const resolved = resolveDistrictForSchool(formData.school || formData.pendingSchool, activeTenant.id, dbSchools, districtsList);
        if (resolved.districtName) {
          setFormData(prev => ({ ...prev, district: resolved.districtName, districtId: resolved.districtId }));
        }
      } else if (districtsList.length === 1 && !formData.district) {
        setFormData(prev => ({ ...prev, district: districtsList[0].name, districtId: districtsList[0].id }));
      }
      const districtIds = districtsList.map(d => d.id);

      // Subscribe to schools
      const schoolsQuery = collection(db, 'schools');
      const unsubscribeSchools = onSnapshot(schoolsQuery, (schoolsSnap) => {
        const matchingSchools = schoolsSnap.docs
          .map(doc => ({ id: doc.id, ...(doc.data() as any) }))
          .filter(school => school.regionId === activeTenant.id || districtIds.includes(school.districtId));

        matchingSchools.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        if (matchingSchools.length > 0) {
          setDbSchools(matchingSchools);
        } else {
          const fallbackSchools = getSchoolsForTenant(activeTenant.id).map(name => ({ id: name, name }));
          fallbackSchools.sort((a, b) => a.name.localeCompare(b.name));
          setDbSchools(fallbackSchools);
        }
      }, (err) => {
        console.warn("Error fetching schools:", err);
        const fallbackSchools = getSchoolsForTenant(activeTenant.id).map(name => ({ id: name, name }));
        fallbackSchools.sort((a, b) => a.name.localeCompare(b.name));
        setDbSchools(fallbackSchools);
      });

      return () => unsubscribeSchools();
    }, (err) => {
      console.warn("Error fetching districts:", err);
      const fallbackDistricts = getDistrictsForTenant(activeTenant.id);
      fallbackDistricts.sort((a, b) => a.localeCompare(b));
      setDbDistricts(fallbackDistricts.map(name => ({ id: name, name })));
      
      const fallbackSchools = getSchoolsForTenant(activeTenant.id).map(name => ({ id: name, name }));
      fallbackSchools.sort((a, b) => a.name.localeCompare(b.name));
      setDbSchools(fallbackSchools);
    });

    return () => {
      unsubscribeDistricts();
    };
  }, [activeTenant.id]);

  const filteredTraditionalSchools = dbSchools.filter(s => {
    const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    return isTraditionalSchool(s.name, s.category, s.level);
  });

  const filteredCareerTechSchools = dbSchools.filter(s => {
    const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    return isCareerTechCenter(s.name, s.category);
  });

  const rawPostSecFromDb = dbSchools.filter(s => {
    const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    return isPostSecondarySchool(s.name, s.category, s.level);
  });

  const regionalPostSecFallback = getPostSecondarySchoolsForRegion(activeTenant.id).filter(s =>
    (s.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPostSecondarySchools = rawPostSecFromDb.length > 0
    ? rawPostSecFromDb
    : regionalPostSecFallback;

  const allTraditionalSchools = dbSchools.filter(s => isTraditionalSchool(s.name, s.category, s.level));
  const allCareerTechSchools = dbSchools.filter(s => isCareerTechCenter(s.name, s.category));
  const otherSchools = dbSchools.filter(s => !allTraditionalSchools.some(t => t.id === s.id || t.name === s.name) && !allCareerTechSchools.some(c => c.id === s.id || c.name === s.name));

  const handleAdvisorSchoolChange = (selectedSchoolName: string) => {
    if (!selectedSchoolName) {
      setFormData(prev => ({
        ...prev,
        pendingSchool: '',
        pendingSchoolId: '',
        school: '',
        schoolId: '',
        district: '',
        districtId: ''
      }));
      return;
    }

    const selectedSchool = dbSchools.find(s => s.name === selectedSchoolName);
    const derivedRegion = selectedSchool ? deriveRegionIdForSchool(selectedSchool, activeTenant.id) : activeTenant.id;
    if (derivedRegion && derivedRegion !== 'global' && derivedRegion !== activeTenant.id) {
      changeTenant(derivedRegion);
    }

    const resolved = resolveDistrictForSchool(selectedSchoolName, derivedRegion || activeTenant.id, dbSchools, dbDistricts);

    setFormData(prev => ({
      ...prev,
      pendingSchool: selectedSchoolName,
      pendingSchoolId: selectedSchool?.id || selectedSchoolName,
      school: selectedSchoolName,
      schoolId: selectedSchool?.id || selectedSchoolName,
      district: resolved.districtName,
      districtId: resolved.districtId
    }));
  };

  const handleAdvisorMultiSchoolChange = (schoolObj: any) => {
    const currentServedNames = formData.schoolsServed || [];
    const currentServedIds = formData.schoolsServedIds || [];
    
    let nextServedNames: string[];
    let nextServedIds: string[];
    
    if (currentServedNames.includes(schoolObj.name)) {
      nextServedNames = currentServedNames.filter(n => n !== schoolObj.name);
      nextServedIds = currentServedIds.filter(id => id !== schoolObj.id);
    } else {
      nextServedNames = [...currentServedNames, schoolObj.name];
      nextServedIds = [...currentServedIds, schoolObj.id];
    }
    
    const primarySchoolName = nextServedNames[0] || '';
    const primarySchoolId = nextServedIds[0] || '';
    
    let assignedDistrictName = '';
    let assignedDistrictId = '';
    
    if (primarySchoolName) {
      const resolved = resolveDistrictForSchool(primarySchoolName, activeTenant.id, dbSchools, dbDistricts);
      assignedDistrictName = resolved.districtName;
      assignedDistrictId = resolved.districtId;
    }
    
    setFormData(prev => ({
      ...prev,
      schoolsServed: nextServedNames,
      schoolsServedIds: nextServedIds,
      school: primarySchoolName,
      schoolId: primarySchoolId,
      pendingSchool: primarySchoolName,
      pendingSchoolId: primarySchoolId,
      district: assignedDistrictName,
      districtId: assignedDistrictId
    }));
  };

  const ensureActiveRegion = () => {
    return !!(activeTenant && activeTenant.id && activeTenant.id !== 'global' && activeTenant.id !== 'apextech');
  };

  const handleNext = () => {
    setSetupError(null);
    if (isMultiTenant && !ensureActiveRegion()) {
      setSetupError("Please select a specific region/county at the top before continuing.");
      setShowRegionSelector(true);
      return;
    }
    if (role === 'student' && step === 4 && formData.academicLevel === 'Post-Secondary') {
      setStep(6);
      return;
    }
    setStep(step + 1);
  };
  const handleBack = () => {
    setSetupError(null);
    if (step === 2) {
      setRole(null);
    }
    if (role === 'student' && step === 6 && formData.academicLevel === 'Post-Secondary') {
      setStep(4);
      return;
    }
    setStep(step - 1);
  };

  const completeSetup = async () => {
    setSetupError(null);
    if (isMultiTenant && !ensureActiveRegion()) {
      setSetupError("Please select a specific region/county at the top before continuing.");
      setShowRegionSelector(true);
      return;
    }
    setIsSubmitting(true);
    try {
      let finalDistrict = formData.district;
      let finalDistrictId = formData.districtId;
      const assignedSchoolName = formData.pendingSchool || formData.school;
      
      let resolvedTenantId = formData.tenantId || (activeTenant && activeTenant.id !== 'global' ? activeTenant.id : undefined);
      let resolvedRegionName = formData.region || (activeTenant && activeTenant.id !== 'global' ? activeTenant.name : undefined);

      if (assignedSchoolName) {
        const resolved = resolveDistrictForSchool(assignedSchoolName, resolvedTenantId, dbSchools, dbDistricts);
        if (!finalDistrict && resolved.districtName) {
          finalDistrict = resolved.districtName;
          finalDistrictId = resolved.districtId;
        }
        if (resolved.regionId && (!resolvedTenantId || resolvedTenantId === 'global')) {
          resolvedTenantId = resolved.regionId;
          resolvedRegionName = formatRegionWithNoun(resolved.regionId);
        }
      }
      if (!resolvedTenantId || resolvedTenantId === 'global') {
        if (activeTenant && activeTenant.id !== 'global') {
          resolvedTenantId = activeTenant.id;
          resolvedRegionName = activeTenant.name;
        }
      }

      if (isAlreadyLoggedIn) {
        const nameToUse = (formData.contactFirstName && formData.contactLastName) 
          ? `${formData.contactFirstName} ${formData.contactLastName}`.trim() 
          : (activeUser.name || 'User');
        const initials = nameToUse.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
        const isUrl = (s: any) => s && typeof s === 'string' && (s.startsWith('http') || s.startsWith('/') || s.startsWith('data:'));

        const studentAttestationPayload = (formData.parentEmail && studentAttested) ? {
          certified: true,
          timestamp: new Date().toISOString(),
          statement: STUDENT_LEGAL_ATTESTATION,
          emailProvided: formData.parentEmail.trim().toLowerCase()
        } : undefined;

        const profileUpdates: any = {
          role: role || 'student',
          name: nameToUse,
          avatar: isUrl(activeUser?.avatar) ? activeUser.avatar : (activeUser?.avatar && activeUser.avatar.length > 1 ? activeUser.avatar : initials),
          profileComplete: true,
          ...formData,
          ...(resolvedTenantId && resolvedTenantId !== 'global' ? {
            tenantId: resolvedTenantId,
            region: resolvedRegionName || formatRegionWithNoun(resolvedTenantId)
          } : {}),
          district: finalDistrict,
          districtId: finalDistrictId,
          ...(studentAttestationPayload ? { studentAttestation: studentAttestationPayload } : {})
        };
        await updateUserProfile(activeUser.uid, profileUpdates);
        setCurrentView('profile');
        onComplete();
      } else {
        const finalFormData: any = { ...formData };
        if (resolvedTenantId && resolvedTenantId !== 'global') {
          finalFormData.tenantId = resolvedTenantId;
          finalFormData.region = resolvedRegionName || formatRegionWithNoun(resolvedTenantId);
        }
        finalFormData.district = finalDistrict;
        finalFormData.districtId = finalDistrictId;
        if (finalFormData.parentEmail && studentAttested) {
          finalFormData.studentAttestation = {
            certified: true,
            timestamp: new Date().toISOString(),
            statement: STUDENT_LEGAL_ATTESTATION,
            emailProvided: finalFormData.parentEmail.trim().toLowerCase()
          };
        }
        await signUpWithRoleAndData(email, password, role || 'student', finalFormData);
        setCurrentView('profile');
        onComplete();
      }
    } catch(e: any) {
      console.error(e);
      let errorMessage = "Profile completion failed. Please try again.";
      if (e.code === 'auth/email-already-in-use') {
        errorMessage = "This email is already linked to an existing account. Please log in instead or use another email.";
      } else if (e.code === 'auth/invalid-email') {
        errorMessage = "The email address provided is invalid.";
      } else if (e.code === 'auth/weak-password') {
        errorMessage = "The password is too weak. Please use a stronger password.";
      } else if (e.code === 'auth/network-request-failed') {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (e.message) {
        errorMessage = e.message;
      }
      setSetupError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic steps calculations
  const totalSteps = role === 'student' 
    ? (isAlreadyLoggedIn ? 7 : 8) 
    : (role === null ? (isAlreadyLoggedIn ? 2 : 3) : (isAlreadyLoggedIn ? 2 : 3));

  const getStepMeta = () => {
    if (step === 1) {
      return {
        title: "ACCOUNT TYPE",
        subtitle: "Select your role in the WBL ecosystem."
      };
    }

    if (role === 'student') {
      const isPostSec = formData.academicLevel === 'Post-Secondary';
      if (step === 1) return { title: "ACCOUNT TYPE", subtitle: "Select your role in the WBL ecosystem." };
      if (step === 2) return { title: "DATE OF BIRTH", subtitle: "Please provide your date of birth for educational verification checks." };
      if (step === 3) return { title: "ACADEMIC LEVEL", subtitle: "Specify your current school level enrollment status." };
      if (step === 4) {
        if (isPostSec) {
          return { title: "POST-SECONDARY INSTITUTION", subtitle: "Select your primary college, university, or technical institution." };
        }
        return { title: "TRADITIONAL HIGH SCHOOL", subtitle: "Select your primary home high school." };
      }
      if (step === 5) return { title: "CAREER & TECH CENTER", subtitle: "Optionally add a regional Career & Technology Center." };
      if (step === 6) return { title: "PERSONAL PROFILE", subtitle: "Provide your contact names and active graduation class year." };
      
      if (isAlreadyLoggedIn) {
        if (step === 7) return { title: "PARENT CONTACT", subtitle: "Provide parent email for FERPA consent." };
      } else {
        if (step === 7) return { title: "SECURITY CREDENTIALS", subtitle: "Create an active secure login for your WBL student account." };
        if (step === 8) return { title: "PARENT CONTACT", subtitle: "Provide parent email for FERPA consent." };
      }
      
      return { title: "SETUP WIZARD", subtitle: "Complete identity initialization." };
    }

    if (step === 2) {
      if (role === 'advisor') {
        return { title: "ADVISOR DETAILS", subtitle: "Complete your academic credentials and high school or career center connection." };
      }
      if (role === 'organization') {
        return { title: "EMPLOYER DETAILS", subtitle: "Onboard your industry corporate body to publish learning rotations." };
      }
      if (role === 'parent') {
        return { title: "PARENT DETAILS", subtitle: "Register parent details to supervise student high school placements." };
      }
    }

    if (step === 3) {
      return { title: "SECURITY CREDENTIALS", subtitle: "Create an active secure login for your WBL account." };
    }

    return { title: "IDENTITY SETUP", subtitle: "Complete identity initialization." };
  };

  const { title, subtitle } = getStepMeta();

  // Premium Role Cards matching user's schematic
  const ROLES_INFO = [
    {
      role: 'student' as UserRole,
      label: 'STUDENT',
      icon: GraduationCap,
      description: 'FIND INTERNSHIPS.'
    },
    {
      role: 'parent' as UserRole,
      label: 'PARENT',
      icon: UserCheck,
      description: 'ADVOCATE FOR YOUR STUDENT.'
    },
    {
      role: 'organization' as UserRole,
      label: 'EMPLOYER',
      icon: Briefcase,
      description: 'FIND TALENT.'
    },
    {
      role: 'advisor' as UserRole,
      label: 'ADVISOR',
      icon: Handshake,
      description: 'GUIDE STUDENTS.'
    }
  ];

  return (
    <div className="flex flex-col w-full max-w-md mx-auto h-full font-sans text-slate-800 px-1 py-1">
      
      {/* Top Header Row with BACK and CLOSE matching the reference mockup */}
      <div className="flex items-center justify-between pb-4 select-none">
        {step > 1 ? (
          <button 
            type="button" 
            onClick={handleBack} 
            className="text-xs font-black tracking-widest text-[#0F2942] hover:text-[#0094c8] py-1.5 flex items-center gap-1 transition-colors uppercase outline-none cursor-pointer"
          >
            <ChevronLeft className="w-3.5 h-3.5 stroke-[3]" /> BACK
          </button>
        ) : (
          <div className="w-10 h-6" /> // Spacer to preserve alignment
        )}
        {showClose ? (
          <button 
            type="button"
            onClick={onComplete} 
            className="text-slate-400 hover:text-slate-800 p-1.5 rounded-full hover:bg-slate-50 transition-all cursor-pointer"
          >
            <X className="w-4 h-4 stroke-[2]" />
          </button>
        ) : (
          <div className="w-10 h-6" /> // Spacer
        )}
      </div>

      {/* Main display Title and Subtitle with gorgeous letter spacing and contrast */}
      <div className="pb-5">
        <h1 className="text-2xl sm:text-3xl font-black text-[#0B2545] uppercase tracking-tight leading-none pt-2">
          {title}
        </h1>
        <p className="text-xs text-slate-400 font-semibold tracking-wide leading-relaxed mt-2">
          {subtitle}
        </p>
      </div>

      {/* Active Region Display & Selection */}
      {isMultiTenant && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full shrink-0 ${activeTenant?.id && activeTenant.id !== 'global' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500 animate-bounce'}`} />
              <div className="flex flex-col">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 leading-none">Your Selected Region</span>
                <span className="text-xs font-bold text-slate-700 uppercase tracking-tight mt-0.5">
                  {activeTenant?.id && activeTenant.id !== 'global' ? activeTenant.name : 'No Region Selected'}
                </span>
              </div>
            </div>
            {/* Show a Change button if they want to switch region */}
            <button
              type="button"
              onClick={() => setShowRegionSelector(!showRegionSelector)}
              className="text-[10px] font-black uppercase tracking-wider text-[#0094c8] hover:underline cursor-pointer"
            >
              {showRegionSelector ? 'Cancel' : 'Change Region'}
            </button>
          </div>

          {(showRegionSelector || (!activeTenant || activeTenant.id === 'global')) && (
            <div className="space-y-1 pt-1.5 border-t border-slate-100">
              <label className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">Choose Region / County</label>
              <div className="relative">
                <MapPin className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select 
                  value={activeTenant?.id === 'global' ? '' : (activeTenant?.id || '')}
                  onChange={(e) => {
                    changeTenant(e.target.value);
                    setShowRegionSelector(false);
                    setSetupError(null);
                  }}
                  className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 hover:border-slate-300 focus:border-[#0094c8] rounded-lg text-slate-800 font-bold outline-none text-xs transition-all appearance-none cursor-pointer"
                >
                  <option value="" disabled>Select your county / region</option>
                  {tenants.filter(t => t.id !== 'apextech' && t.isActive).sort((a, b) => a.name.localeCompare(b.name)).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </div>
              </div>
              {(!activeTenant || activeTenant.id === 'global') && (
                <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider">Please select a specific region to proceed with registration.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Primary Area */}
      <div className="space-y-4">
        
        {/* STEP 1: Select Role Option Cards */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3.5">
              {ROLES_INFO.map(r => {
              const IconComponent = r.icon;
              const hasValidRegion = activeTenant && activeTenant.id && activeTenant.id !== 'global' && activeTenant.id !== 'apextech';
              const isDisabled = !hasValidRegion;
              return (
                <button 
                  key={r.role} 
                  type="button" 
                  disabled={isDisabled}
                  onClick={() => { 
                    if (!hasValidRegion) {
                      setSetupError("Please select a specific region / county before proceeding with registration.");
                      setShowRegionSelector(true);
                      return;
                    }
                    if (r.role === 'parent') {
                      setRole('parent');
                      handleNext();
                      return;
                    }
                    setRole(r.role); 
                    handleNext(); 
                  }} 
                  className={`group w-full py-4 px-4 bg-white border border-slate-100 rounded-xl flex items-center justify-between text-left transition-all duration-200 shadow-xs ${isDisabled ? 'opacity-50 cursor-not-allowed bg-slate-50/50' : 'hover:border-slate-300 hover:bg-slate-50/20 hover:shadow cursor-pointer'}`}
                >
                  <div className="flex items-center">
                    <div className={`w-11 h-11 rounded-lg bg-[#E8F0F8] text-[#1E3E62] flex items-center justify-center mr-4 flex-shrink-0 transition-colors ${isDisabled ? '' : 'group-hover:bg-[#E1EDF8]'}`}>
                      <IconComponent className="w-5 h-5 stroke-[2]" />
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-black text-[#0F2942] tracking-wider uppercase">
                        {r.label}
                      </h3>
                      <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                        {r.description}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* STUDENT ROLE: Custom flow */}
        {role === 'student' && (
          <>
            {/* STEP 2: Age Verification */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="space-y-1.5 pt-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Date of Birth</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})} 
                      value={formData.dateOfBirth || ''}
                      className="w-full px-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200/80 focus:border-slate-800 rounded-xl text-slate-800 font-bold outline-none text-xs transition-all shadow-2xs" 
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold tracking-wide">Students must be at least 13 years old to create an account.</p>
                </div>

                {dobError && (
                  <div className="p-3.5 bg-red-50 text-red-700 border border-red-100 rounded-xl text-xs font-bold leading-normal">
                    ⚠️ {dobError}
                  </div>
                )}

                <button 
                  type="button" 
                  onClick={() => {
                    setDobError(null);
                    if (!formData.dateOfBirth) {
                      setDobError("Please select your date of birth.");
                      return;
                    }
                    const age = calculateAge(formData.dateOfBirth);
                    if (age < 13) {
                      setDobError("Students under 13 are not allowed under current educational guidelines.");
                      return;
                    }
                    setFormData(prev => ({ ...prev, isUnder18: age < 18 }));
                    handleNext();
                  }} 
                  className="w-full py-3.5 bg-[#0B2545] hover:bg-[#1E3E62] text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all duration-200 shadow-md flex items-center justify-center gap-1.5 cursor-pointer mt-4"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* STEP: Parent Contact */}
            {step === totalSteps && (
              <div className="space-y-5">
                {formData.isUnder18 ? (
                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between items-center pb-1">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Parent Email</label>
                      <span className="text-[9px] px-2 py-0.5 bg-red-100 text-red-700 font-extrabold rounded-full uppercase tracking-wider">Required (Under 18)</span>
                    </div>
                    <div className="relative animate-fade-in">
                      <input 
                        type="email" 
                        onChange={(e) => setFormData({...formData, parentEmail: e.target.value})} 
                        value={formData.parentEmail || ''}
                        placeholder="parent@example.com"
                        className="w-full px-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200/80 focus:border-slate-800 rounded-xl text-slate-800 font-bold outline-none text-xs transition-all shadow-2xs" 
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pt-1">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Parent Email Contact</label>
                      <span className="text-[9px] px-2 py-0.5 bg-slate-100 text-slate-600 font-extrabold rounded-full uppercase tracking-wider">Optional (18+)</span>
                    </div>
                    
                    <p className="text-[11px] text-slate-500 font-medium">
                      Would you like to provide a parent or guardian email address to receive career placement alerts and work authorization updates?
                    </p>

                    <div className="flex rounded-xl bg-slate-100 p-1 border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => {
                          setProvideParentEmail(true);
                        }}
                        className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                          provideParentEmail
                            ? 'bg-[#0B2545] text-white shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${provideParentEmail ? 'bg-emerald-400' : 'bg-transparent border border-slate-400'}`} />
                        Yes, Provide Email
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setProvideParentEmail(false);
                          setFormData(prev => ({ ...prev, parentEmail: '' }));
                        }}
                        className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                          !provideParentEmail
                            ? 'bg-[#0B2545] text-white shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${!provideParentEmail ? 'bg-slate-300' : 'bg-transparent border border-slate-400'}`} />
                        No, Skip
                      </button>
                    </div>

                    {provideParentEmail && (
                      <div className="relative animate-fade-in space-y-1.5 pt-1">
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Parent or Guardian Email Address</label>
                        <input 
                          type="email" 
                          onChange={(e) => setFormData({...formData, parentEmail: e.target.value})} 
                          value={formData.parentEmail || ''}
                          placeholder="parent@example.com"
                          className="w-full px-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200/80 focus:border-slate-800 rounded-xl text-slate-800 font-bold outline-none text-xs transition-all shadow-2xs" 
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Distinct Explanation Box to Avoid Confusion */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 text-left space-y-1.5 shadow-2xs">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5 text-[#1E3E62] shrink-0" /> EXPLAINER: STUDENT vs. PARENT EMAIL
                  </span>
                  <p className="text-[11px] text-slate-600 font-bold leading-normal">
                    This <strong className="text-[#1E3E62]">must be your parent or guardian's email address</strong>, not your student email.
                  </p>
                  <p className="text-[11px] text-slate-500 leading-normal">
                    Under educational guidelines (FERPA consent rules), we are required to obtain verifiable parental authorization for students under 18 before arranging career opportunities and work-based placements. Your parent will receive a secure portal link to grant consent.
                  </p>
                </div>

                {/* Mandatory Student Legal Attestation Card */}
                {(formData.isUnder18 || (provideParentEmail && formData.parentEmail)) && (
                  <div className="p-4 bg-amber-50/90 border-2 border-amber-300 rounded-xl text-left space-y-2.5 shadow-xs animate-fade-in">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black uppercase tracking-wider text-amber-900 bg-amber-200/80 px-2 py-0.5 rounded-md">
                        Mandatory Student Legal Attestation
                      </span>
                      <span className="text-[9px] font-bold text-amber-800 uppercase tracking-wider">
                        E-SIGN / UETA
                      </span>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={studentAttested}
                        onChange={(e) => setStudentAttested(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-amber-400 text-[#0B2545] focus:ring-amber-500 accent-[#0B2545] cursor-pointer shrink-0"
                      />
                      <span className="text-[11px] font-semibold text-amber-950 leading-relaxed select-none">
                        "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian."
                      </span>
                    </label>
                  </div>
                )}
                
                {setupError && (
                  <div className="p-3.5 bg-red-50 text-red-700 border border-red-100 rounded-xl text-xs font-bold leading-normal">
                    ⚠️ {setupError}
                  </div>
                )}

                <button 
                  type="button" 
                  disabled={isSubmitting}
                  onClick={() => {
                    setSetupError(null);
                    if (formData.isUnder18 && !formData.parentEmail) {
                      setSetupError("Please provide a parent email address for FERPA consent since you are under 18.");
                      return;
                    }
                    if (!formData.isUnder18 && provideParentEmail && !formData.parentEmail) {
                      setSetupError("Please provide a valid parent email address or choose 'No, Skip'.");
                      return;
                    }
                    if (formData.parentEmail && !studentAttested) {
                      setSetupError("You must certify the legal attestation checkbox regarding your parent or legal guardian's email address to continue.");
                      return;
                    }
                    completeSetup();
                  }} 
                  className={`w-full py-3.5 bg-[#0B2545] text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all duration-200 shadow-md flex items-center justify-center gap-2 mt-4 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-[#1E3E62] cursor-pointer'}`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Registering...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Register Network Profile
                    </>
                  )}
                </button>
              </div>
            )}

            {/* STEP: Student Academic Level */}
            {step === 3 && (
              <div className="space-y-4">
                {activeTenant.highSchoolActive && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setFormData({...formData, academicLevel: 'High School'}); 
                      handleNext();
                    }} 
                    className="group w-full py-4 px-4 bg-white border border-slate-100 rounded-xl flex items-center justify-between text-left transition-all duration-200 shadow-xs hover:border-slate-300 hover:bg-slate-50/20 hover:shadow cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="w-11 h-11 rounded-lg bg-[#E8F0F8] text-[#1E3E62] flex items-center justify-center mr-4 flex-shrink-0 transition-colors group-hover:bg-[#E1EDF8]">
                        <BookOpen className="w-5 h-5 stroke-[2]" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-black text-[#0F2942] tracking-wider uppercase">
                          HIGH SCHOOL STUDENT
                        </h3>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </button>
                )}
                {activeTenant.postSecondaryActive && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setFormData({...formData, academicLevel: 'Post-Secondary'}); 
                      handleNext();
                    }} 
                    className="group w-full py-4 px-4 bg-white border border-slate-100 rounded-xl flex items-center justify-between text-left transition-all duration-200 shadow-xs hover:border-slate-300 hover:bg-slate-50/20 hover:shadow cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="w-11 h-11 rounded-lg bg-[#E8F0F8] text-[#1E3E62] flex items-center justify-center mr-4 flex-shrink-0 transition-colors group-hover:bg-[#E1EDF8]">
                        <GraduationCap className="w-5 h-5 stroke-[2]" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-black text-[#0F2942] tracking-wider uppercase">
                          POST-SECONDARY STUDENT
                        </h3>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </button>
                )}
              </div>
            )}

            {/* STEP 4: Post-Secondary or High School Selector */}
            {step === 4 && role === 'student' && formData.academicLevel === 'Post-Secondary' && (
              <div className="space-y-4">
                <div className="bg-[#E8F0F8] border border-[#B3D4E5] rounded-xl p-3 flex items-center gap-2.5 text-xs text-[#0F2942]">
                  <div className="w-7 h-7 rounded-lg bg-[#0B2545] text-white flex items-center justify-center shrink-0 font-extrabold text-xs">
                    1
                  </div>
                  <div>
                    <span className="font-extrabold block text-slate-800">Primary Post-Secondary Institution (Required)</span>
                    <span className="text-[10px] text-slate-500 font-medium leading-tight">
                      Select your primary college, university, or higher education institution in the region.
                    </span>
                  </div>
                </div>

                <div className="relative pt-1">
                  <span className="absolute left-3.5 top-5 text-slate-400">
                    <Search className="w-3.5 h-3.5" />
                  </span>
                  <input 
                    type="text" 
                    placeholder="Search colleges & universities in region..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full pl-9 pr-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200 focus:border-slate-800 rounded-xl text-slate-800 text-xs font-semibold focus:outline-none transition-all shadow-2xs"
                  />
                </div>
                
                <div className="space-y-1.5 pr-1 max-h-60 overflow-y-auto">
                  {filteredPostSecondarySchools.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4 font-bold">No post-secondary institutions found in this region</p>
                  ) : (
                    filteredPostSecondarySchools.map(school => (
                      <button 
                        key={school.id} 
                        type="button" 
                        onClick={() => {
                          const derivedRegion = deriveRegionIdForSchool(school, activeTenant.id);
                          if (derivedRegion && derivedRegion !== 'global') {
                            changeTenant(derivedRegion);
                          }
                          setSetupError(null);
                          const derivedDist = deriveDistrictForSchoolName(school.name, derivedRegion || activeTenant.id);
                          const matchedDist = dbDistricts.find(d => d.name === derivedDist || d.name.toLowerCase().includes(derivedDist.toLowerCase()));
                          setFormData({
                            ...formData, 
                            tenantId: derivedRegion || activeTenant.id,
                            region: formatRegionWithNoun(derivedRegion || activeTenant.id),
                            school: school.name, 
                            schoolId: school.id,
                            district: matchedDist ? matchedDist.name : derivedDist,
                            districtId: matchedDist ? matchedDist.id : derivedDist
                          });
                          // Post-secondary students advance straight to Personal Profile (step 6)
                          setStep(6);
                        }} 
                        className={`w-full p-3 bg-white border rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between cursor-pointer shadow-2xs ${
                          formData.school === school.name 
                            ? 'border-[#0094c8] bg-[#F0F8FF] text-[#0B2545]' 
                            : 'border-slate-100 hover:border-slate-300 text-slate-700 hover:text-[#0094c8]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-slate-400" />
                          <span>{school.name.replace(/\s*\([^)]*\)\s*/g, '')}</span>
                        </div>
                        <span className="text-[8px] bg-slate-50 text-[#1E3E62] border border-slate-100 px-2.5 py-1 rounded-md font-mono tracking-wider font-extrabold uppercase select-none">SELECT</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* STEP 4: Traditional High School Selector (MANDATORY FOR HIGH SCHOOL STUDENTS) */}
            {step === 4 && role === 'student' && formData.academicLevel !== 'Post-Secondary' && (
              <div className="space-y-4">
                <div className="bg-[#E8F0F8] border border-[#B3D4E5] rounded-xl p-3 flex items-center gap-2.5 text-xs text-[#0F2942]">
                  <div className="w-7 h-7 rounded-lg bg-[#0B2545] text-white flex items-center justify-center shrink-0 font-extrabold text-xs">
                    1
                  </div>
                  <div>
                    <span className="font-extrabold block text-slate-800">Primary Traditional High School (Required)</span>
                    <span className="text-[10px] text-slate-500 font-medium leading-tight">
                      Select your primary home high school in the region.
                    </span>
                  </div>
                </div>

                <div className="relative pt-1">
                  <span className="absolute left-3.5 top-5 text-slate-400">
                    <Search className="w-3.5 h-3.5" />
                  </span>
                  <input 
                    type="text" 
                    placeholder="Search high schools in region..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full pl-9 pr-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200 focus:border-slate-800 rounded-xl text-slate-800 text-xs font-semibold focus:outline-none transition-all shadow-2xs"
                  />
                </div>
                
                <div className="space-y-1.5 pr-1 max-h-60 overflow-y-auto">
                  {filteredTraditionalSchools.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4 font-bold">No high schools found in this region</p>
                  ) : (
                    filteredTraditionalSchools.map(school => (
                      <button 
                        key={school.id} 
                        type="button" 
                        onClick={() => {
                          const derivedRegion = deriveRegionIdForSchool(school, activeTenant.id);
                          if (derivedRegion && derivedRegion !== 'global') {
                            changeTenant(derivedRegion);
                          }
                          setSetupError(null);
                          const derivedDist = deriveDistrictForSchoolName(school.name, derivedRegion || activeTenant.id);
                          const matchedDist = dbDistricts.find(d => d.name === derivedDist || d.name.toLowerCase().includes(derivedDist.toLowerCase()));
                          setFormData({
                            ...formData, 
                            tenantId: derivedRegion || activeTenant.id,
                            region: formatRegionWithNoun(derivedRegion || activeTenant.id),
                            school: school.name, 
                            schoolId: school.id,
                            district: matchedDist ? matchedDist.name : derivedDist,
                            districtId: matchedDist ? matchedDist.id : derivedDist
                          });
                          handleNext();
                        }} 
                        className={`w-full p-3 bg-white border rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between cursor-pointer shadow-2xs ${
                          formData.school === school.name 
                            ? 'border-[#0094c8] bg-[#F0F8FF] text-[#0B2545]' 
                            : 'border-slate-100 hover:border-slate-300 text-slate-700 hover:text-[#0094c8]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span>{school.name.replace(/\s*\([^)]*\)\s*/g, '')}</span>
                        </div>
                        <span className="text-[8px] bg-slate-50 text-[#1E3E62] border border-slate-100 px-2.5 py-1 rounded-md font-mono tracking-wider font-extrabold uppercase select-none">SELECT</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* STEP 5: Career & Tech Center (OPTIONAL SECONDARY SELECTION FOR STUDENTS) */}
            {step === 5 && role === 'student' && (
              <div className="space-y-4">
                {/* Selected Traditional High School Badge */}
                <div className="bg-[#E8F0F8] border border-[#B3D4E5] rounded-xl p-3 flex items-center justify-between text-xs">
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-black uppercase tracking-wider text-[#0F2942] block">Traditional High School (Selected)</span>
                    <span className="font-extrabold text-[#0B2545] flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      {formData.school || 'Not Selected'}
                    </span>
                  </div>
                  <span className="text-[8px] bg-[#0094c8] text-white px-2 py-0.5 rounded font-black uppercase tracking-wider">Required</span>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-700">Add Career & Technology Center</h4>
                  <p className="text-[11px] text-slate-500 font-medium leading-tight">
                    Do you also attend a regional Career & Technology Center (e.g. Swofford, Daniel Morgan, R.D. Anderson)? Select below or skip.
                  </p>
                </div>

                <div className="relative pt-1">
                  <span className="absolute left-3.5 top-5 text-slate-400">
                    <Search className="w-3.5 h-3.5" />
                  </span>
                  <input 
                    type="text" 
                    placeholder="Search career & tech centers in region..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full pl-9 pr-4 py-3 bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200 focus:border-slate-800 rounded-xl text-slate-800 text-xs font-semibold focus:outline-none transition-all shadow-2xs"
                  />
                </div>

                <div className="space-y-1.5 pr-1 max-h-52 overflow-y-auto">
                  {filteredCareerTechSchools.length === 0 ? (
                    <div className="p-4 text-center bg-slate-50 border border-slate-100 rounded-xl">
                      <p className="text-xs text-slate-500 font-bold">No career & tech centers registered in this region.</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">You can proceed with your traditional high school selection.</p>
                    </div>
                  ) : (
                    filteredCareerTechSchools.map(school => {
                      const isSelected = formData.additionalSchools?.includes(school.name);
                      return (
                        <button 
                          key={school.id} 
                          type="button" 
                          onClick={() => {
                            if (isSelected) {
                              setFormData({
                                ...formData,
                                additionalSchools: [],
                                additionalSchoolIds: []
                              });
                            } else {
                              setFormData({
                                ...formData,
                                additionalSchools: [school.name],
                                additionalSchoolIds: [school.id]
                              });
                            }
                          }} 
                          className={`w-full p-3 border rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between cursor-pointer shadow-2xs ${
                            isSelected 
                              ? 'border-emerald-500 bg-emerald-50/50 text-emerald-950 ring-1 ring-emerald-500' 
                              : 'bg-white border-slate-100 hover:border-slate-300 text-slate-700 hover:text-[#0094c8]'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-slate-400" />
                            <span>{school.name}</span>
                          </div>
                          <span className={`text-[8px] px-2.5 py-1 rounded-md font-mono tracking-wider font-extrabold uppercase select-none ${
                            isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-50 text-[#1E3E62] border border-slate-100'
                          }`}>
                            {isSelected ? 'ADDED ✓' : '+ ADD CTC'}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Step 5 Action Controls */}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        additionalSchools: [],
                        additionalSchoolIds: []
                      });
                      handleNext();
                    }}
                    className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-extrabold uppercase tracking-wider text-xs transition-all cursor-pointer text-center"
                  >
                    Skip / No CTC
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNext()}
                    className="flex-1 py-3 bg-[#0B2545] hover:bg-[#1E3E62] text-white rounded-xl font-extrabold uppercase tracking-wider text-xs transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 6: Student Personal Profile Details */}
            {step === 6 && role === 'student' && (
              <div className="space-y-4">
                {/* School Summary Box */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Home High School</span>
                    <span className="font-extrabold text-[#0B2545] text-right">{formData.school}</span>
                  </div>
                  {formData.additionalSchools && formData.additionalSchools.length > 0 && (
                    <div className="flex items-center justify-between border-t border-slate-200/60 pt-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Career & Tech Center</span>
                      <span className="font-extrabold text-emerald-700 text-right">{formData.additionalSchools[0]}</span>
                    </div>
                  )}
                  {formData.district && (
                    <div className="flex items-center justify-between border-t border-slate-200/60 pt-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Assigned District</span>
                      <span className="font-extrabold text-slate-600 text-right text-[11px]">{formData.district}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">First Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. John" 
                      value={formData.contactFirstName || ''}
                      onChange={(e) => setFormData({...formData, contactFirstName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/80 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold transition-all text-slate-850"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Last Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Smith" 
                      value={formData.contactLastName || ''}
                      onChange={(e) => setFormData({...formData, contactLastName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/80 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold transition-all text-slate-850"
                    />
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Graduation Class Year</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 2026" 
                    value={formData.classYear || ''}
                    onChange={(e) => setFormData({...formData, classYear: e.target.value})} 
                    className="w-full px-3.5 py-3 border border-slate-200/80 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold transition-all text-slate-850"
                  />
                </div>
                
                <button 
                  type="button" 
                  onClick={() => {
                    if (!formData.contactFirstName?.trim() || !formData.contactLastName?.trim() || !formData.classYear?.trim()) {
                      showAlert({
                        title: "Incomplete Information",
                        message: "Please fill in first name, last name, and class graduation year.",
                        type: "warning"
                      });
                      return;
                    }
                    handleNext();
                  }} 
                  className="w-full py-3.5 bg-[#0B2545] hover:bg-[#1E3E62] text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all duration-250 shadow-md flex items-center justify-center gap-1.5 cursor-pointer mt-4"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* NON-STUDENT ROLES: Onboarding Details (Step 2) */}
        {role !== null && role !== 'student' && step === 2 && (
          <div className="space-y-4">
            
            {/* ADVISOR Details Form */}
            {role === 'advisor' && (
              <>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">First Name</label>
                    <input 
                      type="text" 
                      placeholder="First Name" 
                      value={formData.contactFirstName || ''}
                      onChange={(e) => setFormData({...formData, contactFirstName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Last Name</label>
                    <input 
                      type="text" 
                      placeholder="Last Name" 
                      value={formData.contactLastName || ''}
                      onChange={(e) => setFormData({...formData, contactLastName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                    High Schools & Career Tech Centers Served (Select All That Apply)
                  </label>
                  
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50">
                    <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-2 space-y-3 bg-white">
                      {allTraditionalSchools.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[9px] font-black uppercase text-[#0B2545] tracking-wider px-2 block pt-1">
                            Traditional High Schools
                          </span>
                          <div className="space-y-1">
                            {allTraditionalSchools.map(s => {
                              const isChecked = (formData.schoolsServed || []).includes(s.name) || formData.school === s.name;
                              return (
                                <label 
                                  key={s.id || s.name} 
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer text-xs transition-all ${
                                    isChecked ? 'bg-blue-50 text-[#0B2545] font-bold' : 'hover:bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  <input 
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleAdvisorMultiSchoolChange(s)}
                                    className="accent-[#0094c8] rounded"
                                  />
                                  <span>{s.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {allCareerTechSchools.length > 0 && (
                        <div className="space-y-1 pt-2">
                          <span className="text-[9px] font-black uppercase text-emerald-800 tracking-wider px-2 block">
                            Career & Technology Centers
                          </span>
                          <div className="space-y-1">
                            {allCareerTechSchools.map(s => {
                              const isChecked = (formData.schoolsServed || []).includes(s.name) || formData.school === s.name;
                              return (
                                <label 
                                  key={s.id || s.name} 
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer text-xs transition-all ${
                                    isChecked ? 'bg-emerald-50 text-emerald-900 font-bold' : 'hover:bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  <input 
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleAdvisorMultiSchoolChange(s)}
                                    className="accent-emerald-600 rounded"
                                  />
                                  <span>{s.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Auto-Assigned District Metadata Feedback */}
                {formData.district && (
                  <div className="p-3 bg-[#E8F0F8] border border-[#B3D4E5] rounded-xl flex items-center justify-between text-xs animate-fade-in">
                    <div className="space-y-0.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 block">Assigned School District</span>
                      <span className="font-extrabold text-[#0B2545] flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-[#0094c8]" />
                        {formData.district}
                      </span>
                    </div>
                    <span className="text-[8px] bg-[#0094c8]/15 text-[#0B2545] border border-[#0094c8]/30 px-2 py-0.5 rounded font-black uppercase tracking-wider">
                      Auto-Assigned
                    </span>
                  </div>
                )}
              </>
            )}

            {/* EMPLOYER/ORGANIZATION Details Form */}
            {role === 'organization' && (
              <>
                {formData.isJoinOrg && (
                  <div className="p-3.5 bg-sky-50 border border-sky-200 rounded-xl space-y-1.5 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-[#0094c8] flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" />
                        Joining Existing Organization Team
                      </span>
                      <span className="text-[8px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                        Team Member
                      </span>
                    </div>
                    <p className="text-xs font-black text-[#011d3c]">{formData.orgName}</p>
                    <div className="text-[9.5px] font-bold text-slate-500 flex flex-wrap items-center gap-x-2 pt-0.5">
                      <span>Primary Region: <strong className="text-slate-800 uppercase">{activeTenant.name}</strong></span>
                      {formData.extendedRegions && formData.extendedRegions.length > 0 && (
                        <span>• Secondary: <strong className="text-slate-800 uppercase">{formData.extendedRegions.join(', ')}</strong></span>
                      )}
                    </div>
                    <p className="text-[9px] text-slate-500 font-medium pt-0.5">
                      You will share postings, applicants, interviews, and reports under this organization.
                    </p>
                  </div>
                )}

                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Organization / Corporate Name</label>
                    {formData.isJoinOrg && (
                      <span className="text-[8.5px] font-black uppercase tracking-wider text-[#0094c8] flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> Locked by Lead
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      type="text" 
                      placeholder="e.g. Spartanburg Health Trust" 
                      value={formData.orgName || ''}
                      onChange={(e) => setFormData({...formData, orgName: e.target.value})} 
                      readOnly={Boolean(formData.isJoinOrg)}
                      className={`w-full px-3.5 py-3 border rounded-xl text-xs font-bold transition-all ${
                        formData.isJoinOrg 
                          ? 'bg-slate-100/90 text-slate-700 border-slate-200 pr-9 cursor-not-allowed'
                          : 'border-slate-200/85 focus:border-slate-850 bg-slate-50/50 hover:bg-slate-50 focus:bg-white text-slate-850'
                      }`}
                      required
                    />
                    {formData.isJoinOrg && (
                      <Lock className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                    )}
                  </div>
                </div>

                <div className="space-y-1.5" id="corporate-address-verified-container">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Corporate Address Location</label>
                    {formData.isJoinOrg && formData.address && (
                      <span className="text-[8.5px] font-black uppercase tracking-wider text-[#0094c8] flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> Corporate HQ
                      </span>
                    )}
                  </div>
                  {formData.isJoinOrg && formData.address ? (
                    <div className="relative">
                      <input 
                        type="text"
                        value={formData.address}
                        readOnly
                        className="w-full px-3.5 py-3 pr-9 border border-slate-200 rounded-xl text-xs bg-slate-100/90 text-slate-700 font-semibold cursor-not-allowed"
                      />
                      <Lock className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                    </div>
                  ) : (
                    <VerifiedAddressInput 
                      value={formData.address || ''}
                      onChange={(val) => setFormData({...formData, address: val})} 
                      placeholder="Headquarters physical address"
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-semibold text-slate-800 transition-all"
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Rep First Name</label>
                    <input 
                      type="text" 
                      placeholder="Liaison first name" 
                      value={formData.contactFirstName || ''}
                      onChange={(e) => setFormData({...formData, contactFirstName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Rep Last Name</label>
                    <input 
                      type="text" 
                      placeholder="Liaison last name" 
                      value={formData.contactLastName || ''}
                      onChange={(e) => setFormData({...formData, contactLastName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Position / Title</label>
                    <input 
                      type="text" 
                      placeholder="e.g. HR Manager / Mentor" 
                      value={formData.professionalTitle || ''}
                      onChange={(e) => setFormData({...formData, professionalTitle: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Direct Phone</label>
                    <input 
                      type="text" 
                      placeholder="(555) 123-4567" 
                      value={formData.contactPhone || ''}
                      onChange={(e) => setFormData({...formData, contactPhone: formatPhoneNumber(e.target.value)})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                    />
                  </div>
                </div>
              </>
            )}

            {/* PARENT/GUARDIAN Details Form */}
            {role === 'parent' && (
              <>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">First Name</label>
                    <input 
                      type="text" 
                      placeholder="Guardian first name" 
                      value={formData.contactFirstName || ''}
                      onChange={(e) => setFormData({...formData, contactFirstName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Last Name</label>
                    <input 
                      type="text" 
                      placeholder="Guardian last name" 
                      value={formData.contactLastName || ''}
                      onChange={(e) => setFormData({...formData, contactLastName: e.target.value})} 
                      className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5" id="parent-address-verified-container">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Physical Home Address</label>
                  <VerifiedAddressInput 
                    id="parent-address-input"
                    value={formData.address || ''}
                    onChange={(val) => setFormData({...formData, address: val})} 
                    placeholder="Enter physical address..."
                    className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-semibold text-slate-800 transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Primary Contact Telephone</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 864-555-0145" 
                    value={formData.parentPhone || ''}
                    onChange={(e) => setFormData({...formData, parentPhone: formatPhoneNumber(e.target.value)})} 
                    className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-850 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-semibold text-slate-800 transition-all"
                    required
                  />
                </div>
              </>
            )}

            <button 
              type="button" 
              onClick={() => {
                console.log('Button clicked, formData:', formData, 'role:', role);
                if (role === 'advisor') {
                  const assignedSchool = formData.pendingSchool || formData.school;
                  const isValid = formData.contactFirstName?.trim() && formData.contactLastName?.trim() && assignedSchool;
                  if (!isValid) {
                    showAlert({
                      title: "Incomplete Information",
                      message: "Please provide your first name, last name, and select your assigned high school or career center.",
                      type: "warning"
                    });
                    return;
                  }
                  if (!formData.district) {
                    const derivedDist = deriveDistrictForSchoolName(assignedSchool, activeTenant.id);
                    const matchedDist = dbDistricts.find(d => d.name === derivedDist || d.name.toLowerCase().includes(derivedDist.toLowerCase()));
                    formData.district = matchedDist ? matchedDist.name : derivedDist;
                    formData.districtId = matchedDist ? matchedDist.id : derivedDist;
                  }
                } else if (role === 'organization') {
                  if (!formData.orgName?.trim() || !formData.address?.trim() || !formData.contactFirstName?.trim() || !formData.contactLastName?.trim()) {
                    showAlert({
                      title: "Incomplete Information",
                      message: "Please complete organization name, address location, and representative contact info.",
                      type: "warning"
                    });
                    return;
                  }
                } else if (role === 'parent') {
                  if (!formData.contactFirstName?.trim() || !formData.contactLastName?.trim() || !formData.address?.trim() || !formData.parentPhone?.trim()) {
                    showAlert({
                      title: "Incomplete Information",
                      message: "Please provide guardian contact name, physical home address, and primary phone number.",
                      type: "warning"
                    });
                    return;
                  }
                }
                if (isAlreadyLoggedIn) {
                  completeSetup();
                } else {
                  handleNext();
                }
              }} 
              className="w-full py-3.5 bg-[#0B2545] hover:bg-[#1E3E62] text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-with duration-200 shadow-md flex items-center justify-center gap-1.5 cursor-pointer mt-4"
            >
              {isAlreadyLoggedIn ? "Complete Profile" : "Continue"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* LAST STEP: Secure Credentials & Access keys */}
        {((role === 'student' && !isAlreadyLoggedIn && step === 7) || (role !== 'student' && !isAlreadyLoggedIn && step === 3)) && (
          <div className="space-y-4">
            
            <div className="space-y-1.5 pt-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Account Access Email</label>
              <input 
                type="email" 
                placeholder={role === 'student' ? "e.g. name@gmail.com (personal email)" : "e.g. name@workspace.org"} 
                value={email}
                onChange={(e) => setEmail(e.target.value)} 
                className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-bold text-slate-850 transition-all"
                required
              />
              {role === 'student' && (
                <div className="space-y-1 mt-1">
                  <p className="text-[10px] text-slate-500 leading-normal">
                    <span className="font-bold text-slate-700">Notice:</span> Students must register using a permanent personal email address (such as Gmail, Outlook, iCloud, or Yahoo). School district email addresses are prohibited as they expire upon graduation.
                  </p>
                  {(() => {
                    const activeTenantData = tenants.find(t => t && t.id === activeTenant.id) || activeTenant;
                    const prohibited = [
                      ...(activeTenantData?.prohibitedEmailDomains || []),
                      ...(globalBranding?.prohibitedEmailDomains || [])
                    ];
                    const check = isEmailDomainProhibited(email, prohibited);
                    if (check.isProhibited) {
                      return (
                        <p className="text-xs text-rose-600 font-bold flex items-center gap-1 mt-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>School district domain (@{check.matchedDomain}) detected. Please use a personal email address.</span>
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
            
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Secure Network Password</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••" autoComplete="new-password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)} 
                  className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-mono text-slate-800 transition-all pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Confirm Network Password</label>
              <div className="relative">
                <input 
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••" autoComplete="new-password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  className="w-full px-3.5 py-3 border border-slate-200/85 focus:border-slate-855 rounded-xl text-xs bg-slate-50/50 hover:bg-slate-50 focus:bg-white outline-none font-mono text-slate-800 transition-all pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                </button>
              </div>
            </div>

            {/* Password Policy list */}
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 mt-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px]">
                <div className="flex items-center gap-1">
                  {hasMinLength ? <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={hasMinLength ? "text-emerald-700 font-bold" : "text-slate-500"}>8+ chars</span>
                </div>
                <div className="flex items-center gap-1">
                  {hasCapital ? <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={hasCapital ? "text-emerald-700 font-bold" : "text-slate-500"}>1+ capital</span>
                </div>
                <div className="flex items-center gap-1">
                  {hasNumber ? <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={hasNumber ? "text-emerald-700 font-bold" : "text-slate-500"}>1+ number</span>
                </div>
                <div className="flex items-center gap-1">
                  {hasSymbol ? <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={hasSymbol ? "text-emerald-700 font-bold" : "text-slate-500"}>1+ symbol</span>
                </div>
                <div className="flex items-center gap-1">
                  {passwordsMatch ? <Check className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={passwordsMatch ? "text-emerald-700 font-bold" : "text-slate-500"}>Match</span>
                </div>
              </div>
            </div>
            
            {setupError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2 shadow-sm animate-fade-in">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="font-medium leading-tight">{setupError}</p>
              </div>
            )}

            {role === 'student' ? (
              <button 
                type="button" 
                onClick={() => {
                  setSetupError(null);
                  if (!email.trim()) {
                    setSetupError("Please provide a valid account access email.");
                    return;
                  }
                  if (role === 'student') {
                    const activeTenantData = tenants.find(t => t && t.id === activeTenant.id) || activeTenant;
                    const prohibited = [
                      ...(activeTenantData?.prohibitedEmailDomains || []),
                      ...(globalBranding?.prohibitedEmailDomains || [])
                    ];
                    const check = isEmailDomainProhibited(email, prohibited);
                    if (check.isProhibited) {
                      setSetupError(`Registration using this email domain (@${check.matchedDomain}) is prohibited for students. School district email accounts expire after graduation. Please register with a personal email address (such as Gmail, Outlook, iCloud, or Yahoo) so you retain lifetime access to your credentials and placements.`);
                      return;
                    }
                  }
                  if (!hasMinLength || !hasCapital || !hasNumber || !hasSymbol) {
                    setSetupError("Your password must meet all requirements (8+ characters, one capital letter, one number, and one symbol).");
                    return;
                  }
                  if (!passwordsMatch) {
                    setSetupError("Passwords do not match. Please verify both passwords.");
                    return;
                  }
                  handleNext();
                }} 
                className="w-full py-3.5 bg-[#0B2545] hover:bg-[#1E3E62] text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all duration-200 shadow-md flex items-center justify-center gap-1.5 cursor-pointer mt-5"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button 
                type="button" 
                disabled={isSubmitting}
                onClick={() => {
                  if (!email.trim()) {
                    setSetupError("Please provide a valid account access email.");
                    return;
                  }
                  if (!hasMinLength || !hasCapital || !hasNumber || !hasSymbol) {
                    setSetupError("Your password must meet all requirements (8+ characters, one capital letter, one number, and one symbol).");
                    return;
                  }
                  if (!passwordsMatch) {
                    setSetupError("Passwords do not match. Please verify both passwords.");
                    return;
                  }
                  completeSetup();
                }} 
                className={`w-full py-3.5 bg-[#0B2545] text-white rounded-xl font-black uppercase tracking-widest text-xs transition-with duration-200 shadow-md flex items-center justify-center gap-2 mt-5 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-[#1E3E62] cursor-pointer'}`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Registering...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Register Network Profile
                  </>
                )}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
